import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { openBillingPortal } from '../services/billingClient';

/**
 * Shown when Stripe subscription is past_due / unpaid. Card updates happen in Stripe Customer Portal only.
 */
const PaymentWarning = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  const { status, email } = location.state || {};

  useEffect(() => {
    if (!status) {
      navigate('/dashboard');
    }
  }, [status, navigate]);

  const getStatusMessage = () => {
    switch (status) {
      case 'past_due':
        return {
          title: 'Payment failed',
          message:
            'We could not charge your card. Update your payment method in the secure Stripe billing portal.',
          action: 'Open billing portal to add or change your card.',
        };
      case 'unpaid':
        return {
          title: 'Account suspended',
          message: 'Your subscription is unpaid after retries. Update your card in Stripe to restore access.',
          action: 'Use the billing portal to pay and reactivate.',
        };
      case 'canceled':
        return {
          title: 'Subscription cancelled',
          message: 'Your subscription is no longer active. You can resubscribe from Plan & billing.',
          action: 'Go to billing to choose a plan or reopen the portal if you still have an invoice open.',
        };
      default:
        return {
          title: 'Billing issue',
          message: 'There is a problem with your subscription billing.',
          action: 'Open the billing portal or visit Plan & billing in your dashboard.',
        };
    }
  };

  const onPortal = async () => {
    setBusy(true);
    try {
      await openBillingPortal();
    } catch (e) {
      alert(e?.message || 'Could not open billing portal. Try Plan & billing from the dashboard.');
      setBusy(false);
    }
  };

  if (!status) return null;

  const statusInfo = getStatusMessage();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8">
          <div className="text-center mb-8">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{statusInfo.title}</h1>
            <p className="text-gray-600">{statusInfo.message}</p>
            {email && (
              <p className="mt-2 text-xs text-gray-500">
                Account: <span className="font-mono">{email}</span>
              </p>
            )}
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Status</span>
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${
                  status === 'past_due'
                    ? 'bg-yellow-100 text-yellow-800'
                    : status === 'unpaid'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-gray-100 text-gray-800'
                }`}
              >
                {status}
              </span>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-blue-800 mb-2">Next step</h3>
            <p className="text-sm text-blue-700">{statusInfo.action}</p>
          </div>

          <div className="space-y-3">
            <button
              type="button"
              disabled={busy}
              onClick={onPortal}
              className="w-full bg-blue-600 text-white px-4 py-3 rounded-md font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {busy ? 'Opening…' : 'Open Stripe billing portal'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/dashboard/settings?plans=1')}
              className="w-full border border-gray-300 text-gray-800 px-4 py-3 rounded-md font-medium hover:bg-gray-50 transition-colors"
            >
              Plan & billing in Devello
            </button>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="w-full bg-gray-200 text-gray-800 px-4 py-3 rounded-md font-medium hover:bg-gray-300 transition-colors"
            >
              Back to dashboard
            </button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Need help?{' '}
              <a href="mailto:support@devellotech.ca" className="text-blue-600 hover:text-blue-700">
                support@devellotech.ca
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentWarning;
